import { NavLink, useLocation } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { NotificationKey } from "@common/state/notificationState";
import { useRecoilValue } from "recoil";
import {
  navBadgeState,
  renderBadgeNumber,
} from "@common/state/badgeNumberState";
import { Body } from "@hypercard/web-components";
import { ChartDonut, Icon } from "@phosphor-icons/react";
import { Home, Reports, Stipends } from "../NavIcon";
import { twMerge } from "@/utils/utils";
import { roleState } from "@common/state/roleState";
import { UserRole } from "@common/utils/graphql";
import { useMemo } from "react";

type ISideNavItemName = "home" | "reports" | "stipends" | "stats";

type ISideNavItem = {
  title: string;
  path: string;
  name: ISideNavItemName;
  notificationKey?: NotificationKey;
  Icon: Icon | (({ className }: { className?: string }) => JSX.Element);
};

type SideNavItemProps = ISideNavItem & { badgeNumber: number };

const SideNavItem = ({ title, path, badgeNumber, Icon }: SideNavItemProps) => {
  const location = useLocation();
  const isActive = location.pathname.trim().includes(path.trim());

  return (
    <NavLink
      to={path}
      className={twMerge(
        "flex flex-col items-center space-y-1.5 text-white transition-colors",
        !isActive && "text-white/30 hover:text-white/50",
      )}
    >
      <div className="flex">
        <Icon className="size-8" />
        {badgeNumber > 0 && (
          <Badge
            variant="destructive"
            className="absolute -translate-y-1 translate-x-5"
          >
            {renderBadgeNumber(badgeNumber)}
          </Badge>
        )}
      </div>
      <Body size="sm" weight="regular">
        {title}
      </Body>
    </NavLink>
  );
};

export const SideNav = () => {
  const navBadgeNumbers = useRecoilValue(navBadgeState);
  const role = useRecoilValue(roleState);
  const isAdmin = role === UserRole.Admin;

  const items = useMemo(() => {
    const base: ISideNavItem[] = [
      {
        title: "Home",
        name: "home",
        path: "/home",
        Icon: Home,
      },
      {
        title: "Reports",
        name: "reports",
        path: "/reports",
        notificationKey: NotificationKey.ReportPending,
        Icon: Reports,
      },
      {
        title: "Stipends",
        name: "stipends",
        path: "/stipends",
        notificationKey: NotificationKey.StipendsPending,
        Icon: Stipends,
      },
    ];

    if (isAdmin) {
      base.push({
        title: "Stats",
        name: "stats",
        path: "/admin/stats",
        Icon: ChartDonut,
      });
    }

    return base;
  }, [isAdmin]);

  return (
    <div className="border-light-gray/50 flex flex-col items-center space-y-8 rounded border bg-[#0E0E0E]/70 px-4 py-6">
      {items.map((item) => (
        <SideNavItem
          {...item}
          badgeNumber={navBadgeNumbers[item.name]}
          key={`navigation-links--link-${item.name}`}
        />
      ))}
    </div>
  );
};
